// TestimonialsSection.js - Version avec animations raffinées
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MdFormatQuote, 
  MdStar, 
  MdStarBorder, 
  MdArrowBack, 
  MdArrowForward,
  MdVerified,
  MdTrendingUp,
  MdAccessTime,
  MdErrorOutline,
  MdCheckCircle,
  MdPeople,
  MdTimeline,
  MdAutoAwesome,
  MdStars
} from 'react-icons/md';
import { FaChartLine, FaUsers, FaClock, FaRocket, FaQuoteLeft, FaQuoteRight, FaInfinity, FaCrown } from 'react-icons/fa';
import { GiAstronautHelmet, GiGalaxy, GiStarsStack, GiSpiralShell, GiCrystalBall } from 'react-icons/gi';
import './TestimonialsSection.css';

const TestimonialsSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [autoplay, setAutoplay] = useState(true);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isCursorActive, setIsCursorActive] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const autoplayRef = useRef(null);
  const sectionRef = useRef(null);
  let cursorTimeout;

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
      setIsCursorActive(true);
      
      clearTimeout(cursorTimeout);
      cursorTimeout = setTimeout(() => setIsCursorActive(false), 100);
    };
    
    const handleScroll = () => {
      if (sectionRef.current) {
        const rect = sectionRef.current.getBoundingClientRect();
        const scrollPercent = Math.min(1, Math.max(0, -rect.top / (rect.height - window.innerHeight)));
        setScrollProgress(scrollPercent);
      }
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(cursorTimeout);
    };
  }, []);

  const testimonials = [
    {
      id: 1,
      name: "Thomas R.",
      role: "Gérant",
      company: "Agence conseil",
      teamSize: "8 personnes",
      image: "https://randomuser.me/api/portraits/men/32.jpg",
      problem: "L'onboarding d'un nouveau client prenait 5 jours de va-et-vient",
      solution: "Avec les workflows mis en place, on est passés à moins d'une journée",
      result: "De 5 jours → 1 jour sur l'onboarding client",
      metrics: [
        { label: "Temps gagné", value: "80%", icon: <MdAccessTime /> },
        { label: "Satisfaction client", value: "+45%", icon: <MdTrendingUp /> }
      ],
      rating: 5,
      tags: ["Onboarding", "Workflows", "Efficacité"],
      color: "#3B82F6",
      gradient: "linear-gradient(135deg, #3B82F6, #1A4FBD)"
    },
    {
      id: 2,
      name: "Marie D.",
      role: "Directrice opérationnelle",
      company: "Cabinet de conseil",
      teamSize: "12 collaborateurs",
      image: "https://randomuser.me/api/portraits/women/44.jpg",
      problem: "On perdait 2 heures par jour en allers-retours, relances et doubles saisies",
      solution: "Aujourd'hui tout roule, les erreurs ont presque disparu",
      result: "2h économisées par jour • Équipe enfin sereine",
      metrics: [
        { label: "Heures économisées", value: "2h/jour", icon: <MdAccessTime /> },
        { label: "Erreurs", value: "-90%", icon: <MdErrorOutline /> }
      ],
      rating: 5,
      tags: ["Automatisation", "Sérénité", "Productivité"],
      color: "#3B82F6",
      gradient: "linear-gradient(135deg, #3B82F6, #1A4FBD)"
    },
    {
      id: 3,
      name: "Sophie L.",
      role: "CEO",
      company: "Startup Tech",
      teamSize: "25 personnes",
      image: "https://randomuser.me/api/portraits/women/68.jpg",
      problem: "Notre croissance ralentissait à cause de process manuels trop lourds",
      solution: "L'automatisation nous a permis de scaler sans embaucher",
      result: "+300% de productivité • Équipe recentrée sur la valeur ajoutée",
      metrics: [
        { label: "Productivité", value: "+300%", icon: <FaRocket /> },
        { label: "ROI", value: "x5", icon: <MdTrendingUp /> }
      ],
      rating: 5,
      tags: ["Scalabilité", "Croissance", "Automatisation"],
      color: "#3B82F6",
      gradient: "linear-gradient(135deg, #3B82F6, #1A4FBD)"
    },
    {
      id: 4,
      name: "David M.",
      role: "Directeur Général",
      company: "Groupe Immobilier",
      teamSize: "50+ personnes",
      image: "https://randomuser.me/api/portraits/men/75.jpg",
      problem: "Les dossiers clients se perdaient dans les emails et WhatsApp",
      solution: "Centralisation et automatisation complète du suivi client",
      result: "0 dossier perdu • 100% de suivi client automatisé",
      metrics: [
        { label: "Dossiers suivis", value: "100%", icon: <MdCheckCircle /> },
        { label: "Temps gagné", value: "15h/semaine", icon: <MdAccessTime /> }
      ],
      rating: 5,
      tags: ["Centralisation", "Suivi client", "Efficacité"],
      color: "#3B82F6",
      gradient: "linear-gradient(135deg, #3B82F6, #1A4FBD)"
    }
  ];

  useEffect(() => {
    if (autoplay) {
      autoplayRef.current = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % testimonials.length);
      }, 6000);
    }
    return () => {
      if (autoplayRef.current) clearInterval(autoplayRef.current);
    };
  }, [autoplay, testimonials.length]);

  const handleNext = () => {
    setAutoplay(false);
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    setTimeout(() => setAutoplay(true), 10000);
  };

  const handlePrev = () => {
    setAutoplay(false);
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
    setTimeout(() => setAutoplay(true), 10000);
  };

  const handleDotClick = (index) => {
    setAutoplay(false);
    setCurrentIndex(index);
    setTimeout(() => setAutoplay(true), 10000);
  };

  const currentTestimonial = testimonials[currentIndex];

  return (
    <section className="testimonials-section-root" ref={sectionRef}>
      <motion.div 
        className={`testimonials-cursor-glow ${isCursorActive ? 'active' : ''}`}
        animate={{
          x: mousePosition.x - 275,
          y: mousePosition.y - 275,
        }}
        transition={{
          type: "spring",
          damping: 30,
          stiffness: 200,
          mass: 0.5
        }}
      />

      <div className="testimonials-particles-container">
        {[...Array(40)].map((_, i) => (
          <div
            key={i}
            className="testimonials-particle"
            style={{
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 18}s`,
              animationDuration: `${22 + Math.random() * 28}s`,
              width: `${2 + Math.random() * 4}px`,
              height: `${2 + Math.random() * 4}px`,
            }}
          />
        ))}
      </div>

      <div className="testimonials-animated-orbs">
        <div className="testimonials-orb testimonials-orb-1" style={{ transform: `translate(${scrollProgress * 100}px, ${scrollProgress * 50}px)` }} />
        <div className="testimonials-orb testimonials-orb-2" style={{ transform: `translate(-${scrollProgress * 80}px, -${scrollProgress * 60}px)` }} />
        <div className="testimonials-orb testimonials-orb-3" style={{ transform: `translate(${scrollProgress * 60}px, -${scrollProgress * 40}px)` }} />
        <div className="testimonials-orb testimonials-orb-4" style={{ transform: `translate(-${scrollProgress * 50}px, ${scrollProgress * 70}px)` }} />
      </div>

      <div className="testimonials-floating-elements">
        <GiGalaxy className="testimonials-floating testimonials-floating-1" />
        <GiSpiralShell className="testimonials-floating testimonials-floating-2" />
        <GiAstronautHelmet className="testimonials-floating testimonials-floating-3" />
        <GiStarsStack className="testimonials-floating testimonials-floating-4" />
        <GiCrystalBall className="testimonials-floating testimonials-floating-5" />
      </div>

      <div className="testimonials-container">
        <motion.div 
          className="testimonials-header"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.2, 0.9, 0.4, 1] }}
        >
          <motion.div 
            className="testimonials-badge-wrapper"
            whileHover={{ scale: 1.03 }}
            transition={{ type: "spring", stiffness: 500, damping: 25 }}
          >
            <span className="testimonials-badge">
              <GiCrystalBall className="testimonials-badge-icon" />
              <span>Témoignages clients</span>
              <MdAutoAwesome className="testimonials-badge-icon" />
            </span>
            <div className="testimonials-badge-glow"></div>
          </motion.div>
          
          <motion.h2 
            className="testimonials-title"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <span className="testimonials-title-line">
              Ils ont <span className="gradient-text">transformé</span>
            </span>
            <span className="testimonials-title-line">
              leur <span className="testimonials-title-highlight">organisation</span>
            </span>
            <div className="testimonials-title-underline"></div>
          </motion.h2>
          
          <motion.p 
            className="testimonials-subtitle"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Découvrez comment nos solutions ont permis à ces entreprises de gagner en efficacité et sérénité.
          </motion.p>
        </motion.div>

        <div className="testimonials-carousel">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentIndex}
              className="testimonial-card-main"
              initial={{ opacity: 0, x: 80 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -80 }}
              transition={{ duration: 0.4, ease: [0.2, 0.9, 0.4, 1] }}
              style={{
                borderColor: `${currentTestimonial.color}20`
              }}
            >
              <div 
                className="testimonial-card-glow"
                style={{ 
                  background: `radial-gradient(circle at 50% 0%, ${currentTestimonial.color}20, transparent 85%)`
                }}
              />

              <div className="quote-icon quote-left">
                <FaQuoteLeft />
              </div>
              <div className="quote-icon quote-right">
                <FaQuoteRight />
              </div>

              <div className="testimonial-problem">
                <div className="problem-header">
                  <MdErrorOutline className="problem-icon" />
                  <span>PROBLÈME</span>
                </div>
                <p className="problem-text">"{currentTestimonial.problem}"</p>
              </div>

              <div className="testimonial-solution">
                <div className="solution-header">
                  <MdCheckCircle className="solution-icon" style={{ color: currentTestimonial.color }} />
                  <span>SOLUTION</span>
                </div>
                <p className="solution-text">"{currentTestimonial.solution}"</p>
              </div>

              <div className="testimonial-result">
                <div className="result-badge" style={{ background: `${currentTestimonial.color}12`, color: currentTestimonial.color }}>
                  <FaRocket />
                  <span>RÉSULTAT CONCRET</span>
                </div>
                <p className="result-text">{currentTestimonial.result}</p>
              </div>

              <div className="metrics-grid">
                {currentTestimonial.metrics.map((metric, idx) => (
                  <div key={idx} className="metric-card">
                    <div className="metric-icon" style={{ color: currentTestimonial.color }}>
                      {metric.icon}
                    </div>
                    <div className="metric-value">{metric.value}</div>
                    <div className="metric-label">{metric.label}</div>
                  </div>
                ))}
              </div>

              <div className="client-info">
                <div className="client-avatar">
                  <img src={currentTestimonial.image} alt={currentTestimonial.name} />
                  <div className="avatar-ring" style={{ borderColor: currentTestimonial.color }}></div>
                </div>
                <div className="client-details">
                  <h4 className="client-name">
                    {currentTestimonial.name}
                    <MdVerified className="verified-badge" style={{ color: currentTestimonial.color }} />
                  </h4>
                  <p className="client-position">
                    {currentTestimonial.role} • {currentTestimonial.company}
                  </p>
                  <div className="client-meta">
                    <MdPeople className="meta-icon" />
                    <span>{currentTestimonial.teamSize}</span>
                  </div>
                  <div className="client-rating">
                    {[...Array(5)].map((_, i) => (
                      i < currentTestimonial.rating ? 
                        <MdStar key={i} className="star filled" /> :
                        <MdStarBorder key={i} className="star" />
                    ))}
                  </div>
                </div>
              </div>

              <div className="testimonial-tags">
                {currentTestimonial.tags.map((tag, idx) => (
                  <span key={idx} className="tag" style={{ color: currentTestimonial.color }}>
                    {tag}
                  </span>
                ))}
              </div>
            </motion.div>
          </AnimatePresence>

          <div className="carousel-controls">
            <motion.button 
              className="nav-btn prev-btn"
              onClick={handlePrev}
              whileHover={{ scale: 1.08 }}
              whileTap={{ scale: 0.95 }}
            >
              <MdArrowBack />
            </motion.button>
            
            <div className="dots-container">
              {testimonials.map((_, idx) => (
                <button
                  key={idx}
                  className={`dot ${currentIndex === idx ? 'active' : ''}`}
                  onClick={() => handleDotClick(idx)}
                  style={{ backgroundColor: currentIndex === idx ? testimonials[idx].color : 'rgba(255,255,255,0.15)' }}
                />
              ))}
            </div>

            <motion.button 
              className="nav-btn next-btn"
              onClick={handleNext}
              whileHover={{ scale: 1.08 }}
              whileTap={{ scale: 0.95 }}
            >
              <MdArrowForward />
            </motion.button>
          </div>
        </div>

        <motion.div 
          className="testimonials-stats"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-value">100%</div>
              <div className="stat-label">Clients satisfaits</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">50+</div>
              <div className="stat-label">Entreprises accompagnées</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">15k+</div>
              <div className="stat-label">Heures économisées</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">4.9/5</div>
              <div className="stat-label">Note moyenne</div>
            </div>
          </div>
        </motion.div>

        <motion.div 
          className="testimonials-cta-wrapper"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <div className="testimonials-cta-card">
            <div className="testimonials-cta-glow"></div>
            <div className="testimonials-cta-pattern"></div>
            
            <div className="cta-content">
              <motion.div 
                className="cta-icon-wrapper"
                animate={{ rotate: 360 }}
                transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
              >
                <FaCrown className="cta-icon" />
              </motion.div>
              <h3>Prêt à <span className="cta-gradient">rejoindre</span> nos clients satisfaits ?</h3>
              <p>Découvrez comment nous pouvons transformer votre organisation</p>
              
              <div className="cta-features">
                <div className="cta-feature">
                  <MdCheckCircle />
                  <span>Diagnostic gratuit</span>
                </div>
                <div className="cta-feature">
                  <MdAccessTime />
                  <span>30 min chrono</span>
                </div>
                <div className="cta-feature">
                  <FaRocket />
                  <span>Résultats garantis</span>
                </div>
              </div>
            </div>
            
            <motion.button 
              className="testimonials-cta-button"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <span>Demander une démo</span>
              <MdArrowForward />
            </motion.button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TestimonialsSection;